/**
 * Localized copy for VAT / tax notes shown under prices.
 * Kept in its own module so the main translation files stay untouched.
 */
export interface VatCopy {
  /** Short note without a rate, e.g. "incl. VAT" */
  inclShort: string;
  /** Template with {rate}, e.g. "incl. {rate}% VAT" */
  inclRate: string;
  /** Template with {rate} and {amount} */
  inclRateAmount: string;
  /** Non-EU export delivery */
  exportFree: string;
  /** EU B2B reverse charge */
  reverseCharge: string;
}

const en: VatCopy = {
  inclShort: 'incl. VAT',
  inclRate: 'incl. {rate}% VAT',
  inclRateAmount: 'incl. {rate}% VAT ({amount})',
  exportFree: 'excl. VAT (export delivery)',
  reverseCharge: '0% VAT — reverse charge',
};

const de: VatCopy = {
  inclShort: 'inkl. MwSt.',
  inclRate: 'inkl. {rate}% MwSt.',
  inclRateAmount: 'inkl. {rate}% MwSt. ({amount})',
  exportFree: 'ohne MwSt. (Ausfuhrlieferung)',
  reverseCharge: '0% MwSt. — Reverse Charge',
};

const es: VatCopy = {
  inclShort: 'IVA incluido',
  inclRate: 'IVA {rate}% incluido',
  inclRateAmount: 'IVA {rate}% incluido ({amount})',
  exportFree: 'sin IVA (entrega de exportación)',
  reverseCharge: '0% IVA — inversión del sujeto pasivo',
};

const fr: VatCopy = {
  inclShort: 'TVA incluse',
  inclRate: 'TVA {rate}% incluse',
  inclRateAmount: 'TVA {rate}% incluse ({amount})',
  exportFree: 'hors TVA (livraison à l’exportation)',
  reverseCharge: '0% TVA — autoliquidation',
};

const it: VatCopy = {
  inclShort: 'IVA inclusa',
  inclRate: 'IVA {rate}% inclusa',
  inclRateAmount: 'IVA {rate}% inclusa ({amount})',
  exportFree: 'IVA esclusa (cessione all’esportazione)',
  reverseCharge: '0% IVA — inversione contabile',
};

const pt: VatCopy = {
  inclShort: 'IVA incluído',
  inclRate: 'IVA {rate}% incluído',
  inclRateAmount: 'IVA {rate}% incluído ({amount})',
  exportFree: 'sem IVA (entrega de exportação)',
  reverseCharge: '0% IVA — autoliquidação',
};

const ru: VatCopy = {
  inclShort: 'включая НДС',
  inclRate: 'включая НДС {rate}%',
  inclRateAmount: 'включая НДС {rate}% ({amount})',
  exportFree: 'без НДС (экспортная поставка)',
  reverseCharge: 'НДС 0% — reverse charge',
};

const sv: VatCopy = {
  inclShort: 'inkl. moms',
  inclRate: 'inkl. {rate}% moms',
  inclRateAmount: 'inkl. {rate}% moms ({amount})',
  exportFree: 'exkl. moms (exportleverans)',
  reverseCharge: '0% moms — omvänd skattskyldighet',
};

const zh: VatCopy = {
  inclShort: '含增值税',
  inclRate: '含 {rate}% 增值税',
  inclRateAmount: '含 {rate}% 增值税（{amount}）',
  exportFree: '不含增值税（出口交付）',
  reverseCharge: '0% 增值税 — 反向征收',
};

const MAP: Record<string, VatCopy> = { en, de, es, fr, it, pt, ru, sv, zh };

export function getVatCopy(language: string): VatCopy {
  return MAP[language] || en;
}
